import React, { Suspense, useEffect, useState } from "react";
import { ChartSocialShareButtonWrapper } from "./style";
import { Button, Flex, SkeletonLoader, Text } from "@hubble/web";

import SocialShareModal from "components/common/socialShareModal";
import { IconArrowOut } from "@hubble/icons";
import NextImage from "components/parts/NextImage";
import {
  generateOGImageBase64ForCoin,
  generateOGParamsForCoin
} from "utils/prices";
import { web } from "@hubble/tokens";
import { mixpanelClient } from "analytics/mixpanel";
import { EVENTS } from "analytics/mixpanel/events";

const ChartSocialShareButton = ({
  coin,
  name,
  range
}: {
  coin: string;
  name: string;
  range?: Array<any>;
}) => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const [OGImageSrc, setOGImageSrc] = useState<string>("");
  const [imgError, setImgError] = useState<boolean>(false);
  const [OGImageAPIData, setOGImageAPIData] = useState<Record<
    string,
    unknown
  > | null>(null);

  const fetchOGImageURL = async () => {
    try {
      const queryParamsRaw = {};
      let finalRange, finalRangePercentDelta;

      if (range) {
        finalRange = range;
        const lastPrice = range[0].y;
        const delta = range[range.length - 1].y - lastPrice;
        finalRangePercentDelta = delta / lastPrice;

        Object.assign(queryParamsRaw, {
          range: finalRange,
          rangePercentDelta: finalRangePercentDelta,
          toDisplayName: name,
          to: coin
        });
      }

      const imageSrc = await generateOGImageBase64ForCoin(queryParamsRaw);
      const coinPriceData = generateOGParamsForCoin(queryParamsRaw);

      setOGImageAPIData(coinPriceData);
      setOGImageSrc(imageSrc);
      setImgError(!imageSrc);
    } catch (error) {
      console.error("Error in fetchOGImageURL:", error);
      setImgError(true);
    }
  };

  useEffect(() => {
    if (showModal) {
      fetchOGImageURL();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  const renderImage = () => {
    if (imgError) {
      return (
        <Flex
          alignItems="center"
          justifyContent="center"
          height="300px"
          width="300px"
          background={web.color.base.gray[100]}
          borderRadius={"20px"}
        >
          <Text.Body
            marketing
            size="xs"
            color={web.color.lightMode.content.secondary}
          >
            Failed to load image
          </Text.Body>
        </Flex>
      );
    }

    return OGImageSrc ? (
      <NextImage
        src={OGImageSrc}
        priority
        alt=""
        style={{ objectFit: "cover", borderRadius: "20px" }}
        height={300}
        width={300}
      />
    ) : (
      <SkeletonLoader height="300px" width="300px" />
    );
  };

  const handleTriggerSocialShareTrigger = () => {
    setShowModal(true);
    mixpanelClient.track(EVENTS.SOCIAL_SHARE_BUTTON_CLICK.name, {
      [EVENTS.SOCIAL_SHARE_BUTTON_CLICK.properties.COIN_NAME]: name
    });
  };

  return (
    <React.Fragment>
      <ChartSocialShareButtonWrapper>
        <Button.Secondary
          size="sm"
          aria-label="social-share-trigger"
          icon={<IconArrowOut />}
          onClick={handleTriggerSocialShareTrigger}
        />
      </ChartSocialShareButtonWrapper>
      <Suspense>
        <SocialShareModal
          OGImageAPIData={OGImageAPIData}
          showModal={showModal}
          setShowModal={setShowModal}
          title={`Share ${name} price`}
          disableCTAs={!OGImageSrc}
          coin={name.toLowerCase().replaceAll(" ", "-")}
        >
          {renderImage()}
        </SocialShareModal>
      </Suspense>
    </React.Fragment>
  );
};

export default ChartSocialShareButton;
